/**
 * Minimal React error boundary. Wraps a subtree so a render error in
 * one piece of UI does not blank out the entire page (Magnis hit this
 * on the search results dropdown — a single bad result blew up the
 * whole app).
 *
 * Usage:
 *   <ErrorBoundary fallback={<small>Search unavailable</small>}>
 *     <SearchResults … />
 *   </ErrorBoundary>
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  readonly children: ReactNode;
  /** Rendered in place of children when the subtree throws. */
  readonly fallback: ReactNode;
}

interface State {
  readonly hasError: boolean;
  readonly error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = { hasError: false };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The Magnis main App suppresses console.warn/error to keep Ink
    // logs clean; routing the boundary's report through console.log
    // (which is NOT suppressed) keeps it visible in DevTools.
    // eslint-disable-next-line no-console
    console.log("[ErrorBoundary]", error.message, info.componentStack);
  }

  public override render(): ReactNode {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}
